/**
 * Activity checkout question catalogue (TRAV23 activities-front merge).
 *
 * Suppliers require per-booking and per-traveler answers (pickup point, traveler ages,
 * dietary needs, ...). The catalogue is fetched once per session and the conditional
 * rules decide which questions apply to the selected rate.
 *
 * Exports are activity-prefixed: `getQuestions` / `IQuestion` are generic enough to
 * collide as this barrel grows, and three such collisions already surfaced during this
 * merge.
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { ActivityCheckoutAPI } from '@share/api';

export interface IActivityQuestion {
  id: string;
  legacyId: number;
  type: string;
  group: string;
  label: string;
  required: string;
  maxLength: number;
  units?: string[];
  hint?: string;
  /** When set, restricts which option codes are valid in this context. */
  allowedAnswers?: string[];
  /** Full option catalogue; used when `allowedAnswers` is absent or empty. */
  options?: { code: string; text: string }[];
}

export interface IActivityQuestionResponse {
  questions: IActivityQuestion[];
  providerId: string;
  success: boolean;
}

export interface IActivityConditionalRule {
  questionId?: string;
  description: string;
  dependencies?: Record<string, string[]>;
  rules?: any[];
  mandatoryWhen?: string;
  reason?: string;
  questionIds?: string[];
  dropOff?: string[];
  pickup?: string[];
  groups?: { id: string; label: string; questions: string[] }[];
}

export interface IActivityConditionalQuestionsResponse {
  providerId: string;
  schemaVersion: string;
  conditionalRules: {
    schemaVersion: string;
    providerId: string;
    conditionalRules: Record<string, IActivityConditionalRule>;
  };
  success: boolean;
}

export interface IActivityQuestionState {
  questionData: IActivityQuestionResponse | null;
  conditionalData: IActivityConditionalQuestionsResponse | null;
  loading: boolean;
  loadingConditional: boolean;
  error: string | null;
}

const initialState: IActivityQuestionState = {
  questionData: null,
  conditionalData: null,
  loading: false,
  loadingConditional: false,
  error: null,
};

export const getActivityQuestions = createAsyncThunk(
  'activityQuestions/getActivityQuestions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ActivityCheckoutAPI.getQuestions();

      // The endpoint reports failure in the body with a 200, not by status.
      if (response.data && response.data.success) {
        return response.data;
      }

      return rejectWithValue('Failed to fetch questions');
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || 'An error occurred',
      );
    }
  },
);

export const getActivityConditionalQuestions = createAsyncThunk(
  'activityQuestions/getActivityConditionalQuestions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await ActivityCheckoutAPI.getConditionalQuestions();

      // This one wraps its single payload in an array, unlike `getQuestions`.
      if (response.data && response.data[0] && response.data[0].success) {
        return response.data[0];
      }

      return rejectWithValue('Failed to fetch conditional questions');
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || error.message || 'An error occurred',
      );
    }
  },
);

const activityQuestionsSlice = createSlice({
  name: 'activityQuestions',
  initialState,
  reducers: {
    resetQuestionState: (state) => {
      state.questionData = null;
      state.conditionalData = null;
      state.loading = false;
      state.loadingConditional = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActivityQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getActivityQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.questionData = action.payload;
      })
      .addCase(getActivityQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(getActivityConditionalQuestions.pending, (state) => {
        state.loadingConditional = true;
        state.error = null;
      })
      .addCase(getActivityConditionalQuestions.fulfilled, (state, action) => {
        state.loadingConditional = false;
        state.conditionalData = action.payload;
      })
      .addCase(getActivityConditionalQuestions.rejected, (state, action) => {
        state.loadingConditional = false;
        state.error = action.payload as string;
      });
  },
});

export const activityQuestionActions = {
  ...activityQuestionsSlice.actions,
  getActivityQuestions,
  getActivityConditionalQuestions,
};

export const activityQuestionReducer = activityQuestionsSlice.reducer;
